import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import i18n from '../i18n';
import { toast } from '../components/ui/Toast';
import { useMapsQuotaStore } from '../stores/mapsQuota.store';
import { readApiCache, writeApiCache } from './offlineCache';
import { ACTING_AGENCY_HEADER, useActingAgency } from '../stores/actingAgency.store';

let accessToken: string | null = null;
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
});

api.interceptors.request.use(config => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  // ADR-0041: inside an agency as Gromly; the server ignores it unless isAdmin.
  const acting = useActingAgency.getState().agency;
  if (acting) config.headers[ACTING_AGENCY_HEADER] = acting.id;
  return config;
});

/**
 * Trip reads are what has to survive a lost connection, and they're the only
 * responses worth keeping: everything else is either live by nature (Places,
 * AI, FX rates), useless offline (billing, community) or a binary this cache
 * has no business storing — the service worker owns those.
 */
function isCacheableRead(config?: InternalAxiosRequestConfig): boolean {
  if (!config) return false;
  if ((config.method ?? 'get').toLowerCase() !== 'get') return false;
  if (config.responseType && config.responseType !== 'json') return false;
  const url = config.url ?? '';
  // The public share view is read by anonymous visitors — there's no owner to
  // key the entry on, and `setCacheOwner` never runs for them.
  if (url.startsWith('/trips/share/')) return false;
  return /^\/trips(\/|$)/.test(url);
}

function cachedResponse(config: InternalAxiosRequestConfig, data: unknown, savedAt: string): AxiosResponse {
  return {
    data,
    status: 200,
    statusText: 'OK (offline cache)',
    headers: { 'x-gromly-offline-cache': savedAt },
    config,
  } as AxiosResponse;
}

api.interceptors.response.use(
  res => {
    if (isCacheableRead(res.config)) {
      void writeApiCache(res.config.url ?? '', res.config.params, res.data);
    }
    return res;
  },
  async error => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && !original.url?.startsWith('/auth/')) {
      if (isRefreshing) {
        return new Promise(resolve => {
          refreshSubscribers.push(token => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(api(original));
          });
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post('/auth/refresh');
        accessToken = data.accessToken;
        onRefreshed(data.accessToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch (refreshError) {
        accessToken = null;
        // A refresh that never reached the server says nothing about the
        // session: bouncing to /login on a dropped connection would throw away
        // an offline session that is still valid once the network is back.
        if ((refreshError as { response?: unknown })?.response) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 429 && error.response?.data?.error === 'MAPS_QUOTA_EXCEEDED') {
      const resetsAt = error.response.data?.details?.resetsAt;
      const date = resetsAt ? new Date(resetsAt).toLocaleDateString() : '';
      // Remember it, not just announce it: the map screens turn this into a
      // proper explanation instead of quietly failing to load anything.
      useMapsQuotaStore.getState().markExceeded(resetsAt);
      toast(i18n.t('maps.quotaExceeded', { date }), 'error');
    }

    // No response at all means the request never got an answer (offline, DNS,
    // server down). A stale trip is worth more than an error screen, so the
    // last copy we stored stands in — callers get a normal 200 and don't need
    // to know. Anything not cached still rejects exactly as before.
    if (!error.response && isCacheableRead(original)) {
      const hit = await readApiCache(original.url ?? '', original.params);
      if (hit) return cachedResponse(original, hit.data, hit.savedAt);
    }

    return Promise.reject(error);
  },
);

/**
 * The machine-readable code the API puts in `error` (`FILE_TOO_LARGE`,
 * `NOTE_NOT_FOUND`…), or null when the request never got an answer.
 */
export function apiErrorCode(e: unknown): string | null {
  const body = (e as { response?: { data?: { error?: unknown } } })?.response?.data;
  return typeof body?.error === 'string' ? body.error : null;
}

/**
 * A delete that comes back 404 already achieved what the caller wanted: the row
 * is not in this trip any more. `requireTripResource` answers 404 both when a
 * co-traveller deleted it first and when the id never belonged to this trip —
 * deliberately, so the API doesn't confirm the existence of other people's rows.
 * Either way the list should stop showing it, so callers treat this as success.
 */
export function isAlreadyGone(e: unknown): boolean {
  return (e as { response?: { status?: number } })?.response?.status === 404;
}
